import { Card, CardContent } from "@bcc/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@bcc/components/ui/tooltip";
import { Clock, TrendingDown, TrendingUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SlaDetailModal } from "./sla-detail-modal";
import { setSlaTrackerCardTestId } from "./sla-tracker-card.utils";

interface SlaData {
  score: number;
  changePercent: number;
  status: "excellent" | "fair" | "needs-attention";
  categoryRatings: {
    fulfillmentSpeed: number;
    orderAccuracy: number;
    responseRate: number;
    onTimeDispatch: number;
  };
}

const mockSlaData: SlaData = {
  score: 92,
  changePercent: 2.5,
  status: "excellent",
  categoryRatings: {
    fulfillmentSpeed: 96,
    orderAccuracy: 99,
    responseRate: 93,
    onTimeDispatch: 95,
  },
};

interface SlaTrackerCardProps {
  testId?: string;
}

export function SlaTrackerCard({ testId }: SlaTrackerCardProps = {}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const slaData = mockSlaData;

  const getStatusConfig = (score: number) => {
    if (score >= 90) {
      return {
        label: t("dashboardComponents.slaTracker.status.excellent"),
        color: "#22C55E",
        bgColor: "bg-[#22C55E]",
        textColor: "text-[#22C55E]",
      };
    } else if (score >= 75) {
      return {
        label: t("dashboardComponents.slaTracker.status.fair"),
        color: "#F59E0B",
        bgColor: "bg-[#F59E0B]",
        textColor: "text-[#F59E0B]",
      };
    } else {
      return {
        label: t("dashboardComponents.slaTracker.status.needsAttention"),
        color: "#EF4444",
        bgColor: "bg-[#EF4444]",
        textColor: "text-[#EF4444]",
      };
    }
  };

  const statusConfig = getStatusConfig(slaData.score);
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference - (slaData.score / 100) * circumference;

  return (
    <div data-testid={testId ?? setSlaTrackerCardTestId("container")}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Card
              onClick={() => setIsModalOpen(true)}
              className="transition-all duration-200 hover:shadow-md hover:-translate-y-1 cursor-pointer border-[#E5E7EB]"
            >
              <CardContent className="p-6">
                <div className="space-y-3">
                  {/* Header with icon */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-[#6B7280]" data-testid={setSlaTrackerCardTestId("title")}>
                      {t("dashboardComponents.slaTracker.title")}
                    </p>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#0D9488]">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Circular Progress Ring */}
                  <div className="flex items-center justify-between">
                    <div className="relative flex items-center justify-center">
                      <svg className="h-24 w-24 -rotate-90" aria-hidden="true">
                        {/* Background ring */}
                        <circle cx="48" cy="48" r="45" stroke="#E5E7EB" strokeWidth="8" fill="none" />
                        {/* Progress ring */}
                        <circle
                          cx="48"
                          cy="48"
                          r="45"
                          stroke={statusConfig.color}
                          strokeWidth="8"
                          fill="none"
                          strokeDasharray={circumference}
                          strokeDashoffset={strokeDashoffset}
                          strokeLinecap="round"
                          className="transition-all duration-500"
                        />
                      </svg>
                      {/* Score text */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-[#111827]">{slaData.score}</span>
                        <span className="text-xs text-[#6B7280]">/ 100</span>
                      </div>
                    </div>

                    {/* Status and trend */}
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`text-sm font-semibold ${statusConfig.textColor}`} data-testid={setSlaTrackerCardTestId("status")}>
                        {statusConfig.label}
                      </span>
                      <div className="flex items-center gap-1">
                        {slaData.changePercent > 0 ? (
                          <TrendingUp className="h-4 w-4 text-[#22C55E]" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-[#EF4444]" />
                        )}
                        <span className={`text-sm font-medium ${slaData.changePercent > 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                          {Math.abs(slaData.changePercent)}%
                        </span>
                      </div>
                      <span className="text-xs text-[#6B7280]">vs last week</span>
                    </div>
                  </div>

                  {/* Subtitle */}
                  <p className="text-xs text-[#6B7280]">Operational reliability</p>
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1">
              <p className="font-semibold">{t("dashboardComponents.slaTracker.slaScore")}</p>
              <p className="text-xs">Based on fulfillment speed, order accuracy, and communication response time.</p>
              <p className="text-xs text-muted-foreground">{t("dashboardComponents.slaTracker.updatedDaily")}</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <SlaDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} slaData={slaData} />
    </div>
  );
}
